import React, { useState, useEffect } from 'react';
import withRoleAccess from '../../hoc/withRoleAccess';
import {
  Card,
  Row,
  Col,
  DatePicker,
  Button,
  Statistic,
  Spin,
  Alert,
  Typography,
  Space,
  Avatar,
  Tag,
  Progress,
  Divider,
  message
} from 'antd';
import {
  UserOutlined,
  TrophyOutlined,
  ReloadOutlined,
  BookOutlined,
  ClockCircleOutlined,
  CalendarOutlined,
  FilePdfOutlined,
  TeamOutlined,
  StarOutlined,
  ReadOutlined
} from '@ant-design/icons';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, PieChart, Pie, Cell, ResponsiveContainer } from 'recharts';
import dayjs from 'dayjs';

const { RangePicker } = DatePicker;
const { Title, Text } = Typography;

const API_URL = process.env.REACT_APP_API_URL;

const TeacherReport = () => {
  const [dateRange, setDateRange] = useState([
    dayjs().subtract(30, 'day'),
    dayjs()
  ]);
  
  const [hoursData, setHoursData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Real API call using fetch
  const fetchHoursData = async () => {
    const token = localStorage.getItem('token');
    const response = await fetch(`${API_URL}/report/teacher-reports/hours?from=${dateRange[0].format('YYYY-MM-DD')}&to=${dateRange[1].format('YYYY-MM-DD')}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    if (!response.ok) throw new Error('Failed to fetch hours data');
    return await response.json();
  };

  const loadData = async () => {
    if (!API_URL) {
      setError('API URL is not configured. Please set REACT_APP_API_URL in your environment variables.');
      return;
    }

    const token = localStorage.getItem('token');
    if (!token) {
      setError('Authentication token not found. Please log in again.');
      return;
    }

    setLoading(true);
    setError(null);
    
    try {
      const hours = await fetchHoursData();
      setHoursData(hours);
      message.success('Teacher report data loaded successfully');
    } catch (err) {
      console.error('Error loading teacher report data:', err);
      
      let errorMessage = 'Failed to load teacher report data';
      if (err.status) {
        if (err.status === 401) {
          errorMessage = 'Authentication failed. Please log in again.';
        } else if (err.status === 400) {
          errorMessage = 'Invalid request parameters';
        } else {
          errorMessage = `Server error: ${err.status}`;
        }
      } else {
        errorMessage = err.message || 'An unexpected error occurred';
      }
      
      setError(errorMessage);
      message.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (dateRange && dateRange.length === 2 && dateRange[0] && dateRange[1]) {
      loadData();
    }
  }, [dateRange]);

  const handleDateRangeChange = (dates) => {
    if (dates && dates.length === 2) {
      setDateRange(dates);
    }
  };

  const handleManualRefresh = () => {
    loadData();
  };

  const handlePrintPDF = () => {
    // Open a new window for printing
    const printWindow = window.open('', '_blank', 'width=800,height=600');
    
    // Get the content to print
    const reportContainer = document.getElementById('teacher-report-container');
    const printContent = reportContainer ? reportContainer.innerHTML : '';
    
    // Create the print document
    const printDocument = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Teacher Performance Report</title>
          <link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/antd/5.0.0/reset.css">
          <style>
            body { 
              font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
              margin: 0;
              padding: 20px;
              background: white;
            }
            .ant-card { 
              box-shadow: none !important; 
              border: 1px solid #d9d9d9 !important;
              margin-bottom: 16px;
              page-break-inside: avoid;
            }
            .ant-statistic-title { font-weight: bold !important; }
            .ant-statistic-content { font-size: 18px !important; }
            .ant-card-head { 
              border-bottom: 2px solid #1890ff !important; 
              background: #f0f2f5 !important;
            }
            .ant-card-head-title { 
              font-weight: bold !important; 
              color: #1890ff !important; 
            }
            .print-header { 
              display: block !important;
              text-align: center; 
              margin-bottom: 20px; 
              border-bottom: 3px solid #1890ff; 
              padding-bottom: 10px; 
            }
            .print-date-range { 
              display: block !important;
              text-align: center; 
              margin-bottom: 15px; 
              font-weight: bold; 
            }
            svg { max-width: 100% !important; height: auto !important; }
            .no-print { display: none !important; }
            @page { 
              margin: 15mm; 
              size: A4; 
            }
          </style>
        </head>
        <body>
          ${printContent}
          <script>
            window.onload = function() {
              window.print();
              window.onafterprint = function() {
                window.close();
              };
            };
          </script>
        </body>
      </html>
    `;
    
    // Write the document and trigger print
    printWindow.document.write(printDocument);
    printWindow.document.close();
  };

  // Prepare chart data
  const topTeachersChartData = hoursData?.top_teachers?.slice(0, 10).map(teacher => {
    const name = teacher.nickname || teacher.teacher_name || 'Unknown';
    return {
      name: name.length > 15 ? name.substring(0, 15) + '...' : name,
      hours: parseFloat(teacher.total_hours) || 0
    };
  }) || [];

  // Prepare subject distribution data
  const subjectDistributionData = hoursData?.top_teachers?.reduce((acc, teacher) => {
    const subject = teacher.most_taught_subject || 'Unknown';
    if (acc[subject]) {
      acc[subject]++;
    } else {
      acc[subject] = 1;
    }
    return acc;
  }, {}) || {};

  const subjectChartData = Object.entries(subjectDistributionData)
    .map(([subject, count], index) => ({
      name: subject,
      value: count,
      color: ['#1890ff', '#52c41a', '#faad14', '#f5222d', '#722ed1', '#fa8c16', '#13c2c2', '#eb2f96', '#a0d911', '#2f54eb'][index % 10]
    }))
    .sort((a, b) => b.value - a.value);

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg">
          <p className="font-semibold">{`${label}`}</p>
          <p className="text-blue-600">
            {`Hours: ${payload[0].value}`}
          </p>
        </div>
      );
    }
    return null;
  };

  const PieTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg">
          <p className="font-semibold">{`${payload[0].name}`}</p>
          <p className="text-blue-600">
            {`Teachers: ${payload[0].value}`}
          </p>
        </div>
      );
    }
    return null;
  };

  // Calculate summary stats
  const totalTeachers = hoursData?.top_teachers?.length || 0;
  const averageHours = parseFloat(hoursData?.average_hours) || 0;
  const totalHours = hoursData?.top_teachers?.reduce((sum, teacher) => sum + parseFloat(teacher.total_hours || 0), 0) || 0;
  const mostActiveTeacher = hoursData?.top_teachers?.[0];
  const totalSubjects = Object.keys(subjectDistributionData).length;

  return (
    <div style={{ padding: '24px', backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
      <div id="teacher-report-container" style={{ maxWidth: '1400px', margin: '0 auto' }}>
        {/* Print Header - Only visible when printing */}
        <div className="print-header" style={{ display: 'none' }}>
          <Title level={2} style={{ margin: 0, color: '#1890ff' }}>
            Teacher Performance Report
          </Title>
          <Text>Generated on {new Date().toLocaleDateString()}</Text>
        </div>

        {/* Print Date Range - Only visible when printing */}
        <div className="print-date-range" style={{ display: 'none' }}>
          Report Period: {dateRange[0]?.format('YYYY-MM-DD')} to {dateRange[1]?.format('YYYY-MM-DD')}
        </div>

        {/* Header */}
        <div style={{ marginBottom: '24px' }}>
          <Title level={2} style={{ marginBottom: '8px' }}>
            Teacher Performance Report
          </Title>
          <Text type="secondary">
            Comprehensive overview of teacher hours and subject specializations
          </Text>
        </div>

        {/* Date Range and Controls */}
        <Card style={{ marginBottom: '24px' }}>
          <Space size="large" wrap>
            <Space>
              <CalendarOutlined />
              <Text strong>Date Range:</Text>
              <RangePicker
                value={dateRange}
                onChange={handleDateRangeChange}
                format="YYYY-MM-DD"
                allowClear={false}
              />
            </Space>
            <Space>
              <Button 
                type="primary" 
                icon={<ReloadOutlined />}
                onClick={handleManualRefresh}
                loading={loading}
              >
                Update Report
              </Button>
              <Button 
                type="default" 
                icon={<FilePdfOutlined />}
                onClick={handlePrintPDF}
                disabled={!hoursData}
              >
                Print to PDF
              </Button>
            </Space>
          </Space>
        </Card>

        {error && (
          <Alert
            message="Error"
            description={error}
            type="error"
            showIcon
            style={{ marginBottom: '24px' }}
          />
        )}

        <Spin spinning={loading}>
          {/* Key Metrics */}
          <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
            <Col xs={24} sm={12} lg={6}>
              <Card>
                <Statistic
                  title="Total Teachers"
                  value={totalTeachers}
                  prefix={<TeamOutlined />}
                  valueStyle={{ color: '#1890ff' }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Card>
                <Statistic
                  title="Total Hours"
                  value={totalHours}
                  precision={2}
                  prefix={<ClockCircleOutlined />}
                  valueStyle={{ color: '#52c41a' }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Card>
                <Statistic
                  title="Average Hours"
                  value={averageHours}
                  precision={2}
                  prefix={<ClockCircleOutlined />}
                  valueStyle={{ color: '#722ed1' }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Card>
                <Statistic
                  title="Subjects Taught"
                  value={totalSubjects}
                  prefix={<BookOutlined />}
                  valueStyle={{ color: '#f5222d' }}
                />
              </Card>
            </Col>
          </Row>

          {/* Charts Row */}
          <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
            {/* Top Teachers by Hours Chart */}
            <Col xs={24} lg={12}>
              <Card 
                title={
                  <Space>
                    <TrophyOutlined style={{ color: '#faad14' }} />
                    Top Teachers by Hours
                  </Space>
                }
              >
                {topTeachersChartData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={topTeachersChartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" angle={-45} textAnchor="end" height={100} />
                      <YAxis />
                      <Tooltip content={<CustomTooltip />} />
                      <Bar dataKey="hours" fill="#52c41a" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div style={{ textAlign: 'center', padding: '60px 0' }}>
                    <Text type="secondary">No teaching hours data available</Text>
                  </div>
                )}
              </Card>
            </Col>

            {/* Subject Distribution Chart */}
            <Col xs={24} lg={12}>
              <Card 
                title={
                  <Space>
                    <ReadOutlined style={{ color: '#1890ff' }} />
                    Subject Distribution
                  </Space>
                }
              >
                {subjectChartData.length > 0 ? (
                  <>
                    <ResponsiveContainer width="100%" height={300}>
                      <PieChart>
                        <Pie
                          data={subjectChartData}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          label={({ name, value, percent }) => `${name} (${value})`}
                          outerRadius={100}
                          fill="#8884d8"
                          dataKey="value"
                        >
                          {subjectChartData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip content={<PieTooltip />} />
                      </PieChart>
                    </ResponsiveContainer>
                    <div style={{ textAlign: 'center', marginTop: '16px' }}>
                      <Space size="small" wrap>
                        {subjectChartData.slice(0, 5).map((entry) => (
                          <Space key={entry.name} size="small">
                            <div 
                              style={{ 
                                width: '12px', 
                                height: '12px', 
                                backgroundColor: entry.color,
                                borderRadius: '50%' 
                              }}
                            />
                            <Text style={{ fontSize: '12px' }}>{entry.name}: {entry.value}</Text>
                          </Space>
                        ))}
                      </Space>
                    </div>
                  </>
                ) : (
                  <div style={{ textAlign: 'center', padding: '60px 0' }}>
                    <Text type="secondary">No subject data available</Text>
                  </div>
                )}
              </Card>
            </Col>
          </Row>

          {/* Performance Insights */}
          <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
            <Col xs={24} lg={12}>
              <Card title="Performance Insights">
                <Space direction="vertical" size="large" style={{ width: '100%' }}>
                  {mostActiveTeacher && (
                    <>
                      <div>
                        <Text strong style={{ color: '#1890ff' }}>Most Active Teacher</Text>
                        <br />
                        <Text>{mostActiveTeacher.nickname || mostActiveTeacher.teacher_name}</Text>
                        <br />
                        <Text type="secondary">
                          {parseFloat(mostActiveTeacher.total_hours).toFixed(2)} hours taught
                        </Text>
                      </div>
                      
                      <Divider />
                      
                      <div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                          <Text>Teacher Productivity</Text>
                          <Text strong style={{ color: '#52c41a' }}>
                            {averageHours > 0 ? ((parseFloat(mostActiveTeacher.total_hours) / averageHours) * 100).toFixed(0) : 0}%
                          </Text>
                        </div>
                        <Progress 
                          percent={averageHours > 0 ? Math.min(((parseFloat(mostActiveTeacher.total_hours) / averageHours) * 100), 100) : 0}
                          strokeColor="#52c41a"
                          showInfo={false}
                        />
                        <Text type="secondary" style={{ fontSize: '12px' }}>
                          Compared to average teacher hours
                        </Text>
                      </div>
                    </>
                  )}
                  
                  <Divider />
                  
                  <Row gutter={16}>
                    <Col span={12}>
                      <Statistic
                        title="Total Minutes"
                        value={totalHours * 60}
                        precision={0}
                        valueStyle={{ fontSize: '18px', color: '#722ed1' }}
                      />
                    </Col>
                    <Col span={12}>
                      <div>
                        <Text type="secondary">Popular Subject</Text>
                        <br />
                        <Text strong style={{ fontSize: '18px' }}>
                          {subjectChartData.length > 0 ? subjectChartData[0].name : 'N/A'}
                        </Text>
                      </div>
                    </Col>
                  </Row>
                </Space>
              </Card>
            </Col>

            {/* Teacher Leaderboard */}
            <Col xs={24} lg={12}>
              <Card title="Teaching Hours Leaderboard">
                {hoursData?.top_teachers?.length > 0 ? (
                  <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                      {hoursData.top_teachers.slice(0, 8).map((teacher, index) => (
                        <Card key={teacher.teacher_id} size="small">
                          <Space align="start" style={{ width: '100%' }}>
                            <Avatar
                              icon={<UserOutlined />}
                              style={{
                                backgroundColor: index === 0 ? '#faad14' : index === 1 ? '#d9d9d9' : index === 2 ? '#cd7f32' : '#1890ff',
                                border: index < 3 ? '2px solid' : 'none',
                                borderColor: index === 0 ? '#faad14' : index === 1 ? '#d9d9d9' : '#cd7f32'
                              }}
                            />
                            <div style={{ flex: 1 }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Text strong>
                                  {teacher.nickname || teacher.teacher_name}
                                  {index === 0 && <StarOutlined style={{ color: '#faad14', marginLeft: 8 }} />}
                                </Text>
                                <Text style={{ color: '#52c41a', fontWeight: 'bold' }}>
                                  {parseFloat(teacher.total_hours).toFixed(2)}h
                                </Text>
                              </div>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '4px' }}>
                                <Text type="secondary">Rank #{index + 1}</Text>
                                {teacher.most_taught_subject && (
                                  <Tag color="blue" style={{ margin: 0 }}>
                                    {teacher.most_taught_subject}
                                  </Tag>
                                )}
                              </div>
                            </div>
                          </Space>
                        </Card>
                      ))}
                    </Space>
                  </div>
                ) : (
                  <div style={{ textAlign: 'center', padding: '40px 0' }}>
                    <Text type="secondary">No teaching hours data available</Text>
                  </div>
                )}
              </Card>
            </Col>
          </Row>
        </Spin>

        <style jsx>{`
          .print-header, .print-date-range {
            display: none;
          }
          
          @media print {
            .print-header, .print-date-range {
              display: block !important;
            }
          }
        `}</style>
      </div>
    </div>
  );
};

export default withRoleAccess(TeacherReport, ['superadmin']);